import { ed25519 } from '@noble/curves/ed25519.js';

export async function computeFingerprint(publicKey: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', publicKey);
  const hashArray = new Uint8Array(hash);
  const hexParts: string[] = [];
  for (let i = 0; i < hashArray.length; i++) {
    const byte = hashArray[i];
    if (byte !== undefined) {
      hexParts.push(byte.toString(16).padStart(2, '0'));
    }
  }
  return hexParts.join('').substring(0, 16).toUpperCase();
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer as ArrayBuffer;
}

function assertLength(bytes: Uint8Array, expected: number, label: string): void {
  if (bytes.byteLength !== expected) {
    throw new Error(`${label} must be ${expected} bytes`);
  }
}

export async function generateEd25519KeyPair(): Promise<{ privateKey: ArrayBuffer; publicKey: ArrayBuffer }> {
  const privateKey = ed25519.utils.randomSecretKey();
  const publicKey = ed25519.getPublicKey(privateKey);
  return {
    privateKey: toArrayBuffer(privateKey),
    publicKey: toArrayBuffer(publicKey)
  };
}

export async function signEd25519(
  privateKey: ArrayBuffer,
  data: ArrayBuffer
): Promise<ArrayBuffer> {
  const privateKeyBytes = new Uint8Array(privateKey);
  assertLength(privateKeyBytes, 32, 'Ed25519 private key');
  return toArrayBuffer(ed25519.sign(new Uint8Array(data), privateKeyBytes));
}

export async function verifyEd25519(
  publicKey: ArrayBuffer,
  signature: ArrayBuffer,
  data: ArrayBuffer
): Promise<boolean> {
  const publicKeyBytes = new Uint8Array(publicKey);
  assertLength(publicKeyBytes, 32, 'Ed25519 public key');
  return ed25519.verify(new Uint8Array(signature), new Uint8Array(data), publicKeyBytes);
}

export async function exportEd25519PublicKey(keyData: ArrayBuffer): Promise<ArrayBuffer> {
  return keyData.slice(0);
}

export async function importEd25519PublicKey(keyData: ArrayBuffer): Promise<ArrayBuffer> {
  const keyBytes = new Uint8Array(keyData);
  assertLength(keyBytes, 32, 'Ed25519 public key');
  return keyData.slice(0);
}
